interface FormatCompactCurrencyParams {
  value: number;
  currencyCode?: string;
  exchangeRate?: number;
  maximumFractionDigits?: number;
}

// Intl.NumberFormat construction is the expensive part and a dashboard re-renders on every
// currency or filter change, so instances are reused.
const formatterCache = new Map<string, Intl.NumberFormat>();

const getFormatter = (currencyCode: string, maximumFractionDigits: number): Intl.NumberFormat => {
  const cacheKey = `${currencyCode}|${maximumFractionDigits}`;
  const cached = formatterCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const formatter = new Intl.NumberFormat('en-US', {
    compactDisplay: 'short',
    currency: currencyCode,
    // Deliberately no minimumFractionDigits: setting it alongside compact notation forces
    // "$127.0M" where the design calls for "$127M".
    maximumFractionDigits,
    notation: 'compact',
    style: 'currency',
  });
  formatterCache.set(cacheKey, formatter);
  return formatter;
};

/**
 * Abbreviated currency for headline figures — `$127M`, `$1.2B`, `-$4.5K`.
 *
 * `formatNumbers` cannot express this: it builds its Intl.NumberFormat without a `notation`
 * pass-through, and adding one there would touch every spreadsheet cell in the app.
 *
 * Note this intentionally ignores the MILLIONS/THOUSANDS unit toggle that `formatNumbers` honours.
 * Dividing by `getCurrencyDivisor` and then compacting would render $127,000,000 as "$127" — off by
 * six orders of magnitude. The exchange rate is applied, because Intl only swaps the symbol.
 */
export const formatCompactCurrency = ({
  value,
  currencyCode = 'USD',
  exchangeRate = 1,
  maximumFractionDigits = 1,
}: FormatCompactCurrencyParams): string =>
  getFormatter(currencyCode, maximumFractionDigits).format(value * exchangeRate);

export default formatCompactCurrency;
